import { Component, ViewChild } from '@angular/core';
import { ApiService } from 'app/api.service';
import { AppService } from 'app/app.service';
import { ModalEditContentComponent } from 'app/modal/modal-edit-content/modal-edit-content.component';
import { UIModalBaseComponent } from 'app/ui/ui-modal-base.component';
import { ModalSelectWorkOrderComponent } from '../modal-select-work-order/modal-select-work-order.component';
import { ModalSelectTaskComponent } from '../modal-select-task/modal-select-task.component';

@Component({
	selector: 'modal-edit-report',
	templateUrl: './modal-edit-report.component.html'
})
export class ModalEditReportComponent extends UIModalBaseComponent {

	@ViewChild(ModalEditContentComponent) editContentModal: ModalEditContentComponent;
	@ViewChild(ModalSelectWorkOrderComponent) selectWorkOrderModal: ModalSelectWorkOrderComponent;
	@ViewChild(ModalSelectTaskComponent) selectTaskModal: ModalSelectTaskComponent;

	id;
	data;
	disabled = false;
	editedProperty;
	task_id;
	work_order_id;
	project_id;

	typTabs = [
		{ id: 'work_order', description: 'Job Report' },
		{ id: 'task', description: 'Task Report' },
	];
	tab = 'work_order';

	templates = [];
	templatedSelected = false;

	isCreatingTemplate = false;
	editingTemplate = null;

	constructor(
		public app: AppService,
		private api: ApiService,
	) {
		super();
	}

	open(mdata: any) {
		this.id = mdata.id || 'new';
		this.task_id = mdata.task_id || null;
		this.work_order_id = mdata.work_order_id || null;
		if (this.task_id) this.tab = 'task';
		this.project_id = mdata.project_id || null;
		this.data = null;
		this.disabled = false;
		this.templates = [];
		this.templatedSelected = false;
		this.isCreatingTemplate = false;

		const success = data => {
			if (data.content) data.content = JSON.parse(data.content);
			this.data = data;
			if (this.id === 'new') this.data.type = this.tab;
			else this.tab = this.data.type;
			this.modal.open();
		};

		const fail = error => {
			this.app.notifications.showDanger(error.message);
		};

		if (this.id === 'new') {
			this.templatedSelected = false;
			this.api.reports.listReportTemplates(list => {
				this.templates = list;
				this.api.reports.new(success, fail);
			}, error => {
				this.app.notifications.showDanger(error.message);
			});
		} else {
			this.templatedSelected = true;
			this.api.reports.get(this.id, success, fail);
		}
	}

	selectTemplate(template, event) {
		const el = event.target as HTMLElement;
		if (
			el.closest(
				'button, [data-toggle="dropdown"], .dropdown-menu, a, input, select, textarea, label, .no-row-toggle'
			)
		) {
			return;
		}
		this.data.id = 'new';
		this.data.name = '';
		if (template) {
			this.data.content = JSON.parse(template.content);
		} else {
			this.data.content = null;
		}
		this.templatedSelected = true;
	}

	save() {
		this.disabled = true;

		if (this.data.id === 'new') {
			const task_id = this.task_id ? this.task_id : this.data.task_id;
			const work_order_id = this.work_order_id ? this.work_order_id : this.data.work_order_id;

			const data = {
				type: this.tab,
				source: task_id ? task_id : work_order_id,
				name: this.data.name,
				content: this.data.content,
				project_id: this.project_id ? this.project_id : this.data.project_id
			};

			this.api.reports.create(data, savedId => {
				this.disabled = false;
				this.app.notifications.showSuccess('Report created.');
				this.modal.close(savedId);
			}, error => {
				this.disabled = false;
				this.app.notifications.showDanger(error.message);
			});
		} else {
			this.api.reports.update(this.data, savedId => {
				this.disabled = false;
				this.app.notifications.showSuccess('Report updated.');
				this.modal.close(savedId);
			}, error => {
				this.disabled = false;
				this.app.notifications.showDanger(error.message);
			});
		}
	}

	delete() {
		this.app.dialog.pop('Delete Report', 'Are you sure you want to delete this report?', ['Cancel', '*Delete'], btn => {
			if (btn === 1) {
				this.disabled = true;
				this.api.reports.delete({ id: this.id }, () => {
					this.disabled = false;
					this.app.notifications.showSuccess('Report deleted.');
					this.modal.close('deleted');
				}, error => {
					this.disabled = false;
					this.app.notifications.showDanger(error.message);
				});
			}
		});
	}

	typeColor(tab) {
		if (this.data.type === tab.id) return 'btn-primary';
		return 'btn-outline-secondary';
	}

	typeChange(id: string) {
		if (this.id !== 'new') return;

		this.data.type = id;
		this.tab = id;
		if (id === 'work_order') {
			this.removeTask();
		} else if (id === 'task') {
			this.removeWorkOrder();
		}
	}

	editContent(property) {
		this.editedProperty = property;
		this.editContentModal.open(this.data[this.editedProperty]);
	}

	editContentModalClosed(data) {
		if (data) {
			this.data[this.editedProperty] = data.content;
		}
	}

	editSelectWorkOrder() {
		this.selectWorkOrderModal.open({
			mode: 'select',
			project_id: null
		});
	}

	removeWorkOrder() {
		this.data.work_order = null;
		this.data.work_order_id = null;
		this.data.project_id = null;
	}

	editSelectWorkOrderModalClosed(data) {
		if (data) {
			this.data.work_order = data;
			this.data.work_order_id = data.id;
			this.data.project_id = data.project_id;
		}
	}

	editSelectTask() {
		this.selectTaskModal.open({
			stage: ['scheduled', 'active', 'completed'],
		});
	}

	removeTask() {
		this.data.task = null;
		this.data.task_id = null;
		this.data.project_id = null;
	}

	editSelectTaskModalClosed(data) {
		if (data) {
			this.data.task = data;
			this.data.task_id = data.id;
			this.data.project_id = data.project_id;
		}
	}

	startCreateTemplate() {
		this.isCreatingTemplate = true;
		this.templatedSelected = true;
		this.editingTemplate = null;
		this.data.id = 'new';
		this.data.name = '';
		this.data.content = null;
	}

	saveTemplate() {
		this.disabled = true;

		const payload: any = {
			template_name: this.data.name,
			content: this.data.content,
		};

		if (this.editingTemplate) {
			payload.id = this.editingTemplate.id;

			this.api.reports.updateTemplate(payload, () => {
				this.disabled = false;
				this.app.notifications.showSuccess('Template updated.');
				this.isCreatingTemplate = false;
				this.templatedSelected = false;
				this.editingTemplate = null;

				this.api.reports.listReportTemplates(list => {
					this.templates = list;
				}, error => {
					this.app.notifications.showDanger(error.message);
				});
			}, error => {
				this.disabled = false;
				this.app.notifications.showDanger(error.message);
			});
		} else {
			this.api.reports.createTemplate(payload, () => {
				this.disabled = false;
				this.app.notifications.showSuccess('Template created.');
				this.isCreatingTemplate = false;
				this.templatedSelected = false;

				this.api.reports.listReportTemplates(list => {
					this.templates = list;
				}, error => {
					this.app.notifications.showDanger(error.message);
				});
			}, error => {
				this.disabled = false;
				this.app.notifications.showDanger(error.message);
			});
		}
	}

	deleteTemplate(template) {
		this.app.dialog.pop(
			'Delete Report Template',
			'Are you sure you want to delete this report template?',
			['Cancel', '*Delete'],
			btn => {
				if (btn === 1) {
					this.disabled = true;
					this.api.reports.deleteTemplate({ id: template?.id }, () => {
						this.disabled = false;
						this.app.notifications.showSuccess('Report template deleted.');
						this.templates = this.templates.filter(t => t.id !== template.id);
					}, error => {
						this.disabled = false;
						this.app.notifications.showDanger(error.message);
					});
				}
			}
		);
	}

	editTemplate(template) {
		this.isCreatingTemplate = true;
		this.templatedSelected = true;
		this.editingTemplate = template;
		this.data.name = template.template_name;

		try {
			this.data.content = template.content ? JSON.parse(template.content) : null;
		} catch (e) {
			this.data.content = template.content;
		}
	}

	onCancel() {
		if (this.disabled) return;

		if (this.isCreatingTemplate) {
			this.isCreatingTemplate = false;
			this.templatedSelected = false;
			this.editingTemplate = null;
			this.data = {
				id: 'new',
				type: 'work_order',
				name: '',
				content: null
			};
			return;
		}
		if (this.id === 'new' && this.templatedSelected) {
			this.templatedSelected = false;

			this.data = {
				id: 'new',
				type: 'work_order',
				name: '',
				content: null
			};
			return;
		}
		this.modal.close();
	}

}
